import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useLayout } from '../contexts/LayoutContext';
import {
  Search,
  Activity,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Wrench,
  MapPin,
  Signal,
  Clock,
  Eye,
  Battery,
  Plug,
  BarChart3,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { Card, Button } from '../components/ui';
import { ViewStationModal } from '../components/stations/ViewStationModal';
import { groupStationsForList } from '../utils/stationsGrouping';
import { getAllFarms, getAllStations, filterStations, findStationById } from '../utils/stationCatalog';
import { useLanguageNavigation } from '../components/routing/LanguageWrapper';
import './StationsPage.css';

const getSignalQuality = (dbm, t) => {
  if (!dbm) return { label: t('stations.signalQuality.unknown'), color: '#6B6B6B' };
  if (dbm > -60) return { label: t('stations.signalQuality.excellent'), color: '#5A7A3A' };
  if (dbm > -75) return { label: t('stations.signalQuality.good'), color: '#7A8A5E' };
  if (dbm > -85) return { label: t('stations.signalQuality.fair'), color: '#8A7A4A' };
  return { label: t('stations.signalQuality.poor'), color: '#8A4A3A' };
};

const getPowerSource = (batteryLevel) => {
  if (batteryLevel !== undefined && batteryLevel !== null && batteryLevel > 0) {
    return { type: 'battery', value: batteryLevel };
  }
  return { type: 'grid', value: null };
};

const getTimeSince = (dateValue, t) => {
  if (!dateValue) return t('stations.never');
  const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
  if (Number.isNaN(date.getTime())) return t('stations.never');
  const minutes = Math.floor((Date.now() - date.getTime()) / (60 * 1000));
  if (minutes < 60) return t('stations.time.minutesAgo', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('stations.time.hoursAgo', { count: hours });
  const days = Math.floor(hours / 24);
  return t('stations.time.daysAgo', { count: days });
};

const getStationTypeClass = (stationType) => {
  if (stationType === 'campo_aperto') return 'station-row__type--campo';
  if (stationType === 'retro_modulo') return 'station-row__type--retro';
  return '';
};

export function StationsPage() {
  const { t } = useTranslation();
  const { navigateTo } = useLanguageNavigation();
  const location = useLocation();
  const { selectedFarm } = useLayout();

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [farmFilter, setFarmFilter] = useState(() => {
    try {
      const stored = localStorage.getItem('stations-farm-filter');
      return stored || 'all';
    } catch (error) {
      return 'all';
    }
  });
  const [viewMode, setViewMode] = useState('list');

  const [expandedBlocks, setExpandedBlocks] = useState(new Set());
  const [expandedRows, setExpandedRows] = useState(new Set());
  const pendingExpansionReset = useRef(true);

  const [viewingStation, setViewingStation] = useState(null);

  const allFarms = useMemo(() => getAllFarms(), []);
  const allStations = useMemo(() => getAllStations(), []);
  const canonicalFarmIds = useMemo(() => new Set(allFarms.map((farm) => farm.id)), [allFarms]);

  const computeStats = (items) => {
    const total = items.length;
    const active = items.filter((station) => station.status === 'active').length;
    const warning = items.filter((station) => station.status === 'warning').length;
    const inactive = items.filter((station) => station.status === 'inactive').length;
    const maintenance = items.filter((station) => station.status === 'warning' || !station.isActive).length;
    const activePercentage = total > 0 ? Math.round((active / total) * 100) : 0;
    return { total, active, warning, inactive, maintenance, activePercentage };
  };

  useEffect(() => {
    if (farmFilter === 'all') return;
    if (canonicalFarmIds.has(farmFilter)) return;
    const fallback = selectedFarm?.id && canonicalFarmIds.has(selectedFarm.id) ? selectedFarm.id : 'all';
    if (fallback !== farmFilter) {
      setFarmFilter(fallback);
    }
  }, [farmFilter, canonicalFarmIds, selectedFarm?.id]);

  useEffect(() => {
    try {
      localStorage.setItem('stations-farm-filter', farmFilter);
      const farmName = farmFilter === 'all'
        ? t('farm.allFarms')
        : allFarms.find((farm) => farm.id === farmFilter)?.name;
      if (farmName) {
        localStorage.setItem('stations-farm-filter-label', farmName);
      }
      window.dispatchEvent(new CustomEvent('stations:farm-filter-changed'));
    } catch (error) {
      // Ignore localStorage errors.
    }
  }, [farmFilter, allFarms, t]);

  useEffect(() => {
    const stationId = location.state?.stationId;
    if (!stationId) return;

    const station = findStationById(stationId);
    try {
      window.history.replaceState({}, document.title);
    } catch (error) {
      // Ignore history replacement errors.
    }
    if (!station) return;

    if (station.farmId && farmFilter !== station.farmId) {
      setFarmFilter(station.farmId);
    }
    if (location.state?.openViewModal) {
      setViewingStation(station);
    }
  }, [location.state, farmFilter]);

  const filteredStations = useMemo(
    () => filterStations({
      farmId: farmFilter,
      status: statusFilter,
      stationType: typeFilter,
      searchQuery,
    }),
    [farmFilter, statusFilter, typeFilter, searchQuery]
  );
  const stats = useMemo(() => computeStats(filteredStations), [filteredStations]);

  const resolvedFarms = useMemo(() => {
    if (allFarms.length > 0) return allFarms;

    const byId = new Map();
    allStations.forEach((station) => {
      if (!station?.farmId) return;
      if (!byId.has(station.farmId)) {
        byId.set(station.farmId, {
          id: station.farmId,
          name: station.farmName || 'Unknown',
        });
      }
    });

    return Array.from(byId.values());
  }, [allFarms, allStations]);

  const farmsToRender = useMemo(() => {
    if (farmFilter === 'all') return resolvedFarms;
    return resolvedFarms.filter((farm) => farm.id === farmFilter);
  }, [farmFilter, resolvedFarms]);

  const groupedStationsByFarm = useMemo(
    () => groupStationsForList({ stations: filteredStations, farms: farmsToRender }),
    [filteredStations, farmsToRender]
  );

  const filterSignature = useMemo(
    () => `${farmFilter}|${statusFilter}|${typeFilter}|${searchQuery.trim().toLowerCase()}`,
    [farmFilter, statusFilter, typeFilter, searchQuery]
  );

  const getBlockKey = (farmId, blockId) => `${farmId}:${blockId}`;

  useEffect(() => {
    pendingExpansionReset.current = true;
    setExpandedRows(new Set());
  }, [filterSignature]);

  useEffect(() => {
    if (!pendingExpansionReset.current) return;

    if (groupedStationsByFarm.length === 0) {
      setExpandedBlocks(new Set());
      pendingExpansionReset.current = false;
      return;
    }

    const initialExpanded = new Set();
    groupedStationsByFarm.forEach(({ farm, blocks }) => {
      if (blocks[0]) {
        initialExpanded.add(getBlockKey(farm.id, blocks[0].id));
      }
    });

    setExpandedBlocks(initialExpanded);
    pendingExpansionReset.current = false;
  }, [groupedStationsByFarm]);

  const toggleBlock = (farmId, blockId) => {
    const key = getBlockKey(farmId, blockId);
    setExpandedBlocks((previous) => {
      const next = new Set(previous);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const toggleRow = (stationId) => {
    setExpandedRows((previous) => {
      const next = new Set(previous);
      if (next.has(stationId)) {
        next.delete(stationId);
      } else {
        next.add(stationId);
      }
      return next;
    });
  };

  const resetFilters = () => {
    setSearchQuery('');
    setStatusFilter('all');
    setTypeFilter('all');
    setFarmFilter('all');
  };

  const hasActiveFilters = Boolean(
    searchQuery.trim() || statusFilter !== 'all' || typeFilter !== 'all' || farmFilter !== 'all'
  );

  const renderStationRow = (station, farmId, blockId) => {
    const signalQuality = getSignalQuality(station.signalStrength, t);
    const powerSource = getPowerSource(station.batteryLevel);
    const detailsId = `station-details-${station.id}`;
    const isRowExpanded = expandedRows.has(station.id);

    return (
      <article key={station.id} className={`station-row ${isRowExpanded ? 'station-row--expanded' : ''}`}>
        <div className="station-row__summary">
          <div className="station-row__identity">
            <span className={`status-indicator status-indicator--${station.status}`} />
            <div className="station-row__identity-text">
              <div className="station-row__identity-header">
                <span className="station-row__code">{station.stationCode}</span>
                <span className={`station-row__type ${getStationTypeClass(station.stationType)}`}>
                  {station.stationType === 'campo_aperto'
                    ? t('stations.types.campo_aperto')
                    : t('stations.types.retro_modulo')}
                </span>
              </div>
              <div className="station-row__location">
                <MapPin size={12} />
                <span>{station.locationDescription || '-'}</span>
              </div>
            </div>
          </div>

          <div className="station-row__chips">
            <span className="station-chip">
              <Signal size={13} style={{ color: signalQuality.color }} />
              <span style={{ color: signalQuality.color }}>{signalQuality.label}</span>
            </span>
            <span className="station-chip">
              {powerSource.type === 'battery' ? (
                <>
                  <Battery size={13} />
                  <span>{powerSource.value}%</span>
                </>
              ) : (
                <>
                  <Plug size={13} />
                  <span>{t('stations.power.gridPowered')}</span>
                </>
              )}
            </span>
            <span className="station-chip">
              <Clock size={13} />
              <span>{getTimeSince(station.lastTransmission, t)}</span>
            </span>
          </div>

          <div className="station-row__actions">
            <button
              type="button"
              className="action-btn action-btn--icon"
              onClick={() => setViewingStation(station)}
              title={t('stations.actions.viewDetails')}
            >
              <Eye size={16} />
            </button>
            <button
              type="button"
              className="action-btn action-btn--icon"
              onClick={() => navigateTo('/map', { state: { stationId: station.id } })}
              title={t('stations.actions.locateOnMap')}
            >
              <MapPin size={16} />
            </button>
            <button
              type="button"
              className="action-btn action-btn--icon"
              onClick={() => navigateTo('/analytics', { state: { stationId: station.id } })}
              title={t('stations.actions.viewAnalytics')}
            >
              <BarChart3 size={16} />
            </button>
            <button
              type="button"
              className="station-row__toggle-btn"
              onClick={() => toggleRow(station.id)}
              aria-expanded={isRowExpanded}
              aria-controls={detailsId}
              data-farm-id={farmId}
              data-block-id={blockId}
            >
              <span>
                {isRowExpanded
                  ? t('stations.actions.hideDetails')
                  : t('stations.actions.showDetails')}
              </span>
              {isRowExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
            </button>
          </div>
        </div>

        {isRowExpanded && (
          <div id={detailsId} className="station-row__details">
            {station.currentReadings ? (
              <div className="station-row__details-grid">
                <div className="station-reading">
                  <span className="station-reading__label">{t('stations.readings.temp')}</span>
                  <span className="station-reading__value">{station.currentReadings.temperature}°C</span>
                </div>
                <div className="station-reading">
                  <span className="station-reading__label">{t('stations.readings.humidity')}</span>
                  <span className="station-reading__value">{station.currentReadings.humidity}%</span>
                </div>
                <div className="station-reading">
                  <span className="station-reading__label">{t('stations.readings.wind')}</span>
                  <span className="station-reading__value">{station.currentReadings.windSpeed} m/s</span>
                </div>
                <div className="station-reading">
                  <span className="station-reading__label">{t('stations.readings.solar')}</span>
                  <span className="station-reading__value">{station.currentReadings.solarRadiation} W/m²</span>
                </div>
              </div>
            ) : (
              <div className="station-row__no-data">{t('stations.noDataAvailable')}</div>
            )}
          </div>
        )}
      </article>
    );
  };

  return (
    <div className="stations-page">
      <div className="stations-stats">
        <div className="stations-stats-grid">
          <Card className="stat-card" padding="md">
            <div className="stat-icon-container stat-icon-container--primary">
              <Activity size={24} />
            </div>
            <div className="stat-label">{t('stations.stats.total')}</div>
            <div className="stat-value">{stats.total}</div>
          </Card>

          <Card className="stat-card" padding="md">
            <div className="stat-icon-container stat-icon-container--success">
              <CheckCircle size={24} />
            </div>
            <div className="stat-label">{t('stations.stats.active')}</div>
            <div className="stat-value">{stats.active}</div>
            <div className="stat-subtitle stat-subtitle--success">{stats.activePercentage}%</div>
          </Card>

          <Card className="stat-card" padding="md">
            <div className="stat-icon-container stat-icon-container--warning">
              <AlertTriangle size={24} />
            </div>
            <div className="stat-label">{t('stations.stats.warning')}</div>
            <div className="stat-value">{stats.warning}</div>
            <div className="stat-subtitle">{t('stations.stats.warningSubtitle')}</div>
          </Card>

          <Card className="stat-card" padding="md">
            <div className="stat-icon-container stat-icon-container--danger">
              <XCircle size={24} />
            </div>
            <div className="stat-label">{t('stations.stats.inactive')}</div>
            <div className="stat-value">{stats.inactive}</div>
            <div className="stat-subtitle">{t('stations.stats.inactiveSubtitle')}</div>
          </Card>

          <Card className="stat-card" padding="md">
            <div className="stat-icon-container stat-icon-container--primary">
              <Wrench size={24} />
            </div>
            <div className="stat-label">{t('stations.stats.maintenance')}</div>
            <div className="stat-value">{stats.maintenance}</div>
            <div className="stat-subtitle">{t('stations.stats.maintenanceSubtitle')}</div>
          </Card>
        </div>
      </div>

      <div className="filters-bar">
        <div className="search-input-wrapper">
          <Search className="search-input-icon" size={20} />
          <input
            type="text"
            className="search-input"
            placeholder={t('stations.searchStations')}
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
          />
        </div>

        <select className="filter-select" value={farmFilter} onChange={(event) => setFarmFilter(event.target.value)}>
          <option value="all">{t('farm.allFarms')}</option>
          {resolvedFarms.map((farm) => (
            <option key={farm.id} value={farm.id}>{farm.name}</option>
          ))}
        </select>

        <select className="filter-select" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
          <option value="all">{t('stations.filters.allStatuses')}</option>
          <option value="active">{t('stations.filters.activeOnly')}</option>
          <option value="warning">{t('stations.filters.warningOnly')}</option>
          <option value="inactive">{t('stations.filters.inactiveOnly')}</option>
        </select>

        <select className="filter-select" value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
          <option value="all">{t('stations.filters.allTypes')}</option>
          <option value="campo_aperto">{t('stations.types.campo_aperto')}</option>
          <option value="retro_modulo">{t('stations.types.retro_modulo')}</option>
        </select>

        {hasActiveFilters && (
          <button type="button" className="stations-clear-filters-btn" onClick={resetFilters}>
            {t('stations.filters.clearActive')}
          </button>
        )}

        <div className="view-toggle">
          <button
            type="button"
            className={`view-toggle-btn ${viewMode === 'list' ? 'view-toggle-btn--active' : ''}`}
            onClick={() => setViewMode('list')}
          >
            {t('stations.view.list')}
          </button>
          <button
            type="button"
            className={`view-toggle-btn ${viewMode === 'table' ? 'view-toggle-btn--active' : ''}`}
            onClick={() => setViewMode('table')}
          >
            {t('stations.view.table')}
          </button>
        </div>
      </div>

      {viewMode === 'list' && (
        <div className="stations-list">
          {groupedStationsByFarm.map(({ farm, blocks }) => (
            <section key={farm.id} className="stations-farm-group">
              <div className="stations-farm-header">
                <h3 className="stations-farm-title">{farm.name}</h3>
                <span className="stations-farm-count">
                  {t('stations.section.stationCount', {
                    count: blocks.reduce((sum, block) => sum + block.stations.length, 0),
                  })}
                </span>
              </div>

              <div className="stations-block-list">
                {blocks.map((block) => {
                  const blockKey = getBlockKey(farm.id, block.id);
                  const isBlockExpanded = expandedBlocks.has(blockKey);
                  const blockContentId = `block-content-${farm.id}-${block.id}`;

                  return (
                    <div key={blockKey} className="stations-block-group">
                      <button
                        type="button"
                        className="stations-block-header"
                        onClick={() => toggleBlock(farm.id, block.id)}
                        aria-expanded={isBlockExpanded}
                        aria-controls={blockContentId}
                      >
                        <span className="stations-block-header-left">
                          {isBlockExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                          <span className="stations-block-title">{block.name}</span>
                        </span>
                        <span className="stations-block-count">
                          {t('stations.section.stationCount', { count: block.stations.length })}
                        </span>
                      </button>

                      {isBlockExpanded && (
                        <div id={blockContentId} className="stations-block-content">
                          {block.stations.map((station) => renderStationRow(station, farm.id, block.id))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          ))}

          {filteredStations.length === 0 && (
            <div className="stations-empty-state">
              <Activity size={64} />
              <h3 className="empty-state-title">{t('stations.empty.title')}</h3>
              <p className="empty-state-message">{t('stations.empty.message')}</p>
              {hasActiveFilters && (
                <Button variant="secondary" onClick={resetFilters}>
                  {t('stations.empty.clearFilters')}
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {viewMode === 'table' && (
        <div className="stations-table-container">
          <div className="stations-table-header">
            <div>{t('stations.table.name')}</div>
            <div>{t('stations.table.type')}</div>
            <div>{t('stations.table.location')}</div>
            <div>{t('stations.table.status')}</div>
            <div>{t('stations.table.power')}</div>
            <div>{t('stations.table.signal')}</div>
            <div>{t('stations.table.lastUpdate')}</div>
            <div>{t('stations.table.actions')}</div>
          </div>

          {filteredStations.map((station) => {
            const signalQuality = getSignalQuality(station.signalStrength, t);
            const powerSource = getPowerSource(station.batteryLevel);

            return (
              <div key={station.id} className="stations-table-row">
                <div className="table-cell-station">
                  <div className={`table-status-dot table-status-dot--${station.status}`} />
                  <span className="table-station-code">{station.stationCode}</span>
                </div>

                <div className="table-cell-type">
                  <span className="table-type-badge">
                    {station.stationType === 'campo_aperto'
                      ? t('stations.types.campo_aperto')
                      : t('stations.types.retro_modulo')}
                  </span>
                </div>

                <div className="table-cell-location">{station.locationDescription}</div>

                <div className="table-cell-status">
                  {station.status === 'active' && (
                    <span className="table-status-badge table-status-badge--active">
                      <CheckCircle size={12} />
                      {t('stations.status.active')}
                    </span>
                  )}
                  {station.status === 'warning' && (
                    <span className="table-status-badge table-status-badge--warning">
                      <AlertTriangle size={12} />
                      {t('stations.status.warning')}
                    </span>
                  )}
                  {station.status === 'inactive' && (
                    <span className="table-status-badge table-status-badge--inactive">
                      <XCircle size={12} />
                      {t('stations.status.inactive')}
                    </span>
                  )}
                </div>

                <div className="table-cell-power">
                  {powerSource.type === 'battery' ? (
                    <div className="table-inline-icon">
                      <Battery size={14} />
                      <span>{powerSource.value}%</span>
                    </div>
                  ) : (
                    <div className="table-inline-icon">
                      <Plug size={14} />
                      <span>{t('stations.power.grid')}</span>
                    </div>
                  )}
                </div>

                <div className="table-cell-signal">
                  <Signal size={14} style={{ color: signalQuality.color }} />
                  <span style={{ color: signalQuality.color }}>{signalQuality.label}</span>
                </div>

                <div className="table-cell-update">
                  {station.lastTransmission ? getTimeSince(station.lastTransmission, t) : t('stations.never')}
                </div>

                <div className="table-cell-actions">
                  <button
                    type="button"
                    className="table-action-button"
                    title={t('common.view')}
                    onClick={() => setViewingStation(station)}
                  >
                    <Eye size={16} />
                  </button>

                  <button
                    type="button"
                    className="table-action-button"
                    title={t('stations.actions.locateOnMap')}
                    onClick={() => navigateTo('/map', { state: { stationId: station.id } })}
                  >
                    <MapPin size={16} />
                  </button>

                  <button
                    type="button"
                    className="table-action-button"
                    title={t('stations.actions.viewAnalytics')}
                    onClick={() => navigateTo('/analytics', { state: { stationId: station.id } })}
                  >
                    <BarChart3 size={16} />
                  </button>
                </div>
              </div>
            );
          })}

          {filteredStations.length === 0 && (
            <div className="table-empty-state">
              <Activity size={64} />
              <h3 className="empty-state-title">{t('stations.empty.title')}</h3>
              <p className="empty-state-message">{t('stations.empty.message')}</p>
            </div>
          )}
        </div>
      )}

      <ViewStationModal
        isOpen={!!viewingStation}
        onClose={() => setViewingStation(null)}
        station={viewingStation}
      />
    </div>
  );
}
